import { useState } from "react";
import { HelpCircle, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

/**
 * Centralized help content for the entire application.
 * Add new entries here to make them available across all pages.
 */
export const HELP_CONTENT = {
  // ═══════════════════════════════════════════════════════════════
  // DASHBOARD & SUBMISSIONS
  // ═══════════════════════════════════════════════════════════════
  submission_status: {
    title: "Submission Status",
    description: "Tracks the lifecycle of a new matter enquiry: Draft → Pending → In Progress → Complete. A submission becomes 'Complete' when the matter is opened.",
  },
  matter_reference: {
    title: "Matter Reference",
    description: "The unique identifier for a matter in your practice management system (e.g., BL.VEC.001.0001). Format: Firm.Client.Matter.Variant",
  },
  fee_earner: {
    title: "Fee Earner",
    description: "The solicitor responsible for this matter. They receive notifications and are accountable for progressing the file.",
  },
  provisional_id: {
    title: "Matter Reference",
    description: "The unique matter reference for this file. New references use BL.XXX.YYY.NNNN format. Legacy references use OB-YYYY-XXXX format.",
  },

  // ═══════════════════════════════════════════════════════════════
  // DASHBOARD BUTTONS / FORM TYPES
  // ═══════════════════════════════════════════════════════════════
  btn_forf_risk: {
    title: "FORF & Risk Assessment",
    description: "Start a new matter: run conflict check, send client onboarding, generate File Opening Request Form (FORF) and Client Care Letter (CCL).",
    link: { text: "Go to form", href: "/new-matter" },
  },
  btn_pre_caseminder: {
    title: "Pre-CaseMinder",
    description: "Register an existing matter that predates CaseMinder. Allows you to run conflict checks and generate CCLs for legacy files.",
    link: { text: "Go to form", href: "/pre-caseminder" },
  },
  btn_risk_assessment: {
    title: "Risk Assessment",
    description: "Standalone or mid-point risk review for an active matter. Use when circumstances change or for periodic compliance reviews.",
    link: { text: "Go to form", href: "/risk-assessment" },
  },
  btn_invoice_request: {
    title: "Invoice Request",
    description: "Request Accounts to raise an invoice for this matter. Specify amount, narrative, and disbursements.",
    link: { text: "Go to form", href: "/invoice-request" },
  },
  btn_payment_request: {
    title: "Payment Request",
    description: "Request a single payment from client account during the matter. This now covers completion-funds requests too — the legacy Completion Funds page is being retired.",
    link: { text: "Go to form", href: "/payment-request" },
  },
  btn_completion_statement: {
    title: "Completion Statement",
    description: "Generate a financial completion statement showing all funds in/out. Required before completion on property transactions.",
    link: { text: "Go to form", href: "/completion-statement" },
  },
  btn_funds_request: {
    title: "Completion Funds Request (retired)",
    description: "Replaced by Payment Request. Existing instructions remain viewable. Instruct Accounts on fund flows at completion. Based on your Completion Statement figures.",
    link: { text: "Go to form", href: "/accounts-instruction" },
  },
  btn_post_completion: {
    title: "Post-Completion Checklist",
    description: "Track tasks after exchange/completion: SDLT submission, Land Registry applications, certificate filings.",
    link: { text: "Go to form", href: "/post-completion" },
  },
  btn_file_closure: {
    title: "File Closure",
    description: "Close a matter with final billing reconciliation and compliance checks. Archives the file.",
    link: { text: "Go to form", href: "/file-closure" },
  },

  // ═══════════════════════════════════════════════════════════════
  // CONFLICT CHECKS
  // ═══════════════════════════════════════════════════════════════
  conflict_check: {
    title: "Conflict Check",
    description: "A mandatory search to identify any conflicts of interest before accepting a new client or matter. Required by SRA regulations.",
    link: { text: "Run a conflict check", href: "/conflict-check" },
  },
  conflict_status: {
    title: "Conflict Status",
    description: "Clear = No conflicts found. Clear with Conditions = Minor issues that can be managed. Conflict = Cannot proceed without resolution.",
  },
  conflicts_cleared: {
    title: "Conflicts Cleared",
    description: "Number of conflict checks that passed (clear or clear with conditions) for NEW matters only. Existing matter conflict checks are excluded as they don't follow the standard onboarding pipeline.",
  },
  search_query: {
    title: "Search Query",
    description: "The terms used to search for conflicts - typically client name, company name, and related parties. More specific queries yield more accurate results.",
  },
  completing_person: {
    title: "Person Completing the Check",
    description: "Who is completing this conflict check. Defaults to you. If you are an authorised approver (a decision maker), you can approve the clearance yourself once saved and no separate approval email is sent. Recorded in the audit trail alongside the approver.",
  },
  sharepoint_conflicts_list: {
    title: "SharePoint Conflicts List",
    description: "The central register of all matters and clients stored in SharePoint. Used to check for potential conflicts against new enquiries.",
  },
  conflict_override: {
    title: "Conflict Override",
    description: "When a potential conflict is identified but can be managed (e.g., with information barriers), a senior partner can override to allow the matter to proceed.",
  },

  // ═══════════════════════════════════════════════════════════════
  // CLIENT ONBOARDING
  // ═══════════════════════════════════════════════════════════════
  ob_status: {
    title: "Onboarding Status",
    description: "Tracks client verification progress: Sent → Partial → Complete. Clients must complete AML/KYC checks before work can begin.",
    link: { text: "View onboarding queue", href: "/dashboard?tab=onboarding" },
  },
  ob_complete: {
    title: "OB Complete",
    description: "Client onboardings that reached 'complete' status, meaning the client has submitted all required AML/KYC documents and passed verification.",
  },
  ob_sent: {
    title: "OB Sent",
    description: "Onboarding forms that have been emailed to the client but not yet started or completed.",
  },
  ob_partial: {
    title: "OB Partial",
    description: "Client has started the onboarding form but hasn't completed all required sections. May need a reminder.",
  },
  thirdfort: {
    title: "Thirdfort Verification",
    description: "Third-party AML/ID verification service. Status shows whether the client has completed identity checks through Thirdfort.",
  },
  aml_check: {
    title: "AML Check",
    description: "Anti-Money Laundering verification. Required by law to verify client identity and source of funds before accepting instructions.",
  },
  kyc: {
    title: "KYC (Know Your Client)",
    description: "The process of verifying a client's identity and assessing potential risks. Includes ID verification, address proof, and source of funds.",
  },
  send_ob: {
    title: "Send Onboarding",
    description: "Sends the client onboarding form via email. The client will receive a secure link to complete their AML/KYC requirements.",
  },
  resend_ob: {
    title: "Resend Onboarding",
    description: "Send another copy of the onboarding link to the client. Use if the original email was lost or expired.",
  },
  ob_email_delivery: {
    title: "Email Delivery Status",
    description: "Shows whether the onboarding email was delivered to the client. Success = delivered. Failed = delivery error, consider resending. Historical = record created before email tracking was implemented.",
  },
  ob_historical: {
    title: "Historical Record",
    description: "This onboarding record was created before email delivery tracking was implemented. No delivery status data is available. The onboarding itself may still be valid.",
  },
  ob_deduplication: {
    title: "Onboarding Deduplication",
    description: "The Client Onboarding tracker automatically shows only the most recent record per client email address, preventing duplicate entries from cluttering the view.",
  },

  // ═══════════════════════════════════════════════════════════════
  // FORF (FILE OPENING REQUEST FORM)
  // ═══════════════════════════════════════════════════════════════
  forf: {
    title: "FORF",
    description: "File Opening Request Form - the document that authorises opening a new matter. Must be approved before work commences.",
  },
  forf_ready: {
    title: "FORF Readiness",
    description: "FORF = File Opening Request Form. 'Ready' means the draft has been created for review. 'Not Ready' means the fee earner hasn't generated the FORF yet.",
  },
  forf_draft: {
    title: "Draft FORF",
    description: "A preliminary FORF that has been generated but not yet signed or approved. Needs review before submission.",
  },
  start_forf: {
    title: "Start FORF",
    description: "Initiates the File Opening Request Form process. Only available after conflict check is cleared and onboarding is complete.",
  },
  view_forf: {
    title: "View FORF",
    description: "Opens the File Opening Request Form PDF in a new tab. You can review, download, or print from there.",
  },

  // ═══════════════════════════════════════════════════════════════
  // CCL (CLIENT CARE LETTER)
  // ═══════════════════════════════════════════════════════════════
  ccl: {
    title: "CCL (Client Care Letter)",
    description: "The engagement letter sent to clients outlining terms of business, fee estimates, and regulatory information. Required by SRA.",
  },
  generate_ccl: {
    title: "Generate CCL",
    description: "Creates a Client Care Letter based on matter details. The letter will be saved to SharePoint and can be sent to the client.",
  },
  ccl_editor: {
    title: "CCL Editor",
    description: "Edit the Client Care Letter content before sending. You can customise fee estimates, scope of work, and special terms.",
  },

  // ═══════════════════════════════════════════════════════════════
  // ANALYTICS & CONVERSIONS
  // ═══════════════════════════════════════════════════════════════
  matters_created: {
    title: "Matters Created",
    description: "New matters opened in the practice management system after successful client onboarding. This is the final stage of the new client pipeline.",
  },
  conversion_cc_ob: {
    title: "CC → OB Conversion",
    description: "Percentage of cleared conflicts that resulted in completed onboardings. Values >100% can occur when some OBs were initiated before the reporting period.",
  },
  conversion_ob_matter: {
    title: "OB → Matter Conversion",
    description: "Percentage of completed onboardings that became active matters. Low rates may indicate bottlenecks in matter opening procedures.",
  },
  ageing: {
    title: "Ageing Buckets",
    description: "Shows how long onboardings have been waiting at 'sent' or 'partial' status. High numbers in 15+ days suggest clients aren't completing their forms.",
  },
  league_table: {
    title: "Fee Earner League",
    description: "Performance ranking showing each fee earner's conversion funnel. Conv. Rate = (OB Complete ÷ OB Sent) × 100. Higher is better.",
  },
  avg_days: {
    title: "Average Days",
    description: "Mean number of days from OB sent to OB complete. Lower numbers indicate faster client response and follow-up.",
  },

  // ═══════════════════════════════════════════════════════════════
  // EMAIL FILING (OUTLOOK ADD-IN)
  // ═══════════════════════════════════════════════════════════════
  file_to_sharepoint: {
    title: "File to SharePoint",
    description: "Saves the email (as .msg file) to the matter's Attendances folder in SharePoint. Creates an audit trail of client communications.",
  },
  attendances_folder: {
    title: "Attendances Folder",
    description: "The SharePoint subfolder where emails are saved. Located within each matter folder for organised document management.",
  },
  include_attachments: {
    title: "Include Attachments",
    description: "When ON, email attachments are saved separately to the Documents folder. Useful for keeping attachments accessible.",
  },
  auto_delete: {
    title: "Auto-Delete After Filing",
    description: "Automatically moves the email to Deleted Items after successful filing. Keeps your inbox clean.",
  },
  conflict_check_btn: {
    title: "Conflict Check",
    description: "Runs a quick conflict search before filing. Recommended for new client emails to ensure no conflicts exist.",
  },

  // ═══════════════════════════════════════════════════════════════
  // TIME RECORDING
  // ═══════════════════════════════════════════════════════════════
  time_recording: {
    title: "Time Recording",
    description: "Records billable time to LawBillity. Each email filed can generate a time entry for the associated work.",
  },
  time_unit: {
    title: "Time Unit",
    description: "Standard billing increment. 1 unit = 0.1 hours (6 minutes). Most emails are recorded as 1-2 units.",
  },
  time_narrative: {
    title: "Time Narrative",
    description: "Description of work done, appears on client bills. Should be professional and specific. AI can generate these automatically.",
  },
  time_template: {
    title: "Time Template",
    description: "Pre-set narrative styles: Considering (reviewing correspondence), Drafting (writing response), Reviewing (checking documents), Attendance (call/meeting).",
  },

  // ═══════════════════════════════════════════════════════════════
  // RISK ASSESSMENT
  // ═══════════════════════════════════════════════════════════════
  risk_assessment: {
    title: "Risk Assessment",
    description: "Evaluation of potential risks associated with a matter. Required for compliance and helps identify high-risk clients or transactions.",
    link: { text: "Complete a risk assessment", href: "/risk-assessment" },
  },
  risk_level: {
    title: "Risk Level",
    description: "Low = Standard procedures apply. Medium = Enhanced due diligence needed. High = Senior partner approval required.",
  },
  source_of_funds: {
    title: "Source of Funds",
    description: "Documentation showing where the client's money comes from. Required for AML compliance, especially for property transactions.",
  },
  source_of_wealth: {
    title: "Source of Wealth",
    description: "How the client accumulated their overall wealth (salary, inheritance, business sale). Different from source of funds for this transaction.",
  },
  pep: {
    title: "PEP (Politically Exposed Person)",
    description: "A person holding a prominent public position. Requires enhanced due diligence due to higher corruption risk.",
  },
  sanctions_check: {
    title: "Sanctions Check",
    description: "Verification that the client is not on any government sanctions lists. Mandatory for all new clients.",
  },

  // ═══════════════════════════════════════════════════════════════
  // SDLT / STAMP DUTY
  // ═══════════════════════════════════════════════════════════════
  sdlt: {
    title: "SDLT",
    description: "Stamp Duty Land Tax - tax paid on property purchases in England and Northern Ireland. Amount depends on property value and buyer circumstances.",
  },
  sdlt_form: {
    title: "SDLT Questionnaire",
    description: "Client form to collect information needed for Stamp Duty calculations. Covers buyer status, property details, and reliefs.",
  },
  first_time_buyer: {
    title: "First Time Buyer",
    description: "Someone who has never owned property before. May qualify for SDLT relief on purchases up to £625,000.",
  },
  additional_property: {
    title: "Additional Property",
    description: "A second home or buy-to-let. Subject to 3% SDLT surcharge on top of standard rates.",
  },
  sdlt_return: {
    title: "SDLT Return",
    description: "The form submitted to HMRC declaring stamp duty. Must be filed within 14 days of completion.",
  },

  // ═══════════════════════════════════════════════════════════════
  // POST INTAKE / DEADLINES
  // ═══════════════════════════════════════════════════════════════
  post_intake: {
    title: "Post Intake",
    description: "Processing of physical mail. Posts are scanned, OCR'd, and queued for fee earner review and deadline extraction.",
  },
  deadline_extraction: {
    title: "Deadline Extraction",
    description: "AI-powered identification of important dates in correspondence. Flags court deadlines, response dates, and limitation periods.",
  },
  post_status: {
    title: "Post Status",
    description: "Pending = Awaiting review. Filed = Saved to matter folder (Claim & File does both in one press). Claimed = picked up but not yet on the matter.",
  },

  // ═══════════════════════════════════════════════════════════════
  // COMPLETION & ACCOUNTS
  // ═══════════════════════════════════════════════════════════════
  completion_date: {
    title: "Completion Date",
    description: "The date when the property transaction completes. Funds transfer and keys are released on this date.",
  },
  exchange_date: {
    title: "Exchange Date",
    description: "The date when contracts are exchanged. After exchange, both parties are legally committed to the transaction.",
  },
  completion_statement: {
    title: "Completion Statement",
    description: "Financial summary of all money in and out of the transaction. Shows balance due from/to client at completion.",
  },
  client_account: {
    title: "Client Account",
    description: "The firm's bank account holding client money. Separate from the office account. Regulated by SRA Accounts Rules.",
  },
  office_account: {
    title: "Office Account",
    description: "The firm's business account for bills and expenses. Distinguished from client account for regulatory purposes.",
  },
  disbursements: {
    title: "Disbursements",
    description: "Expenses paid on behalf of the client (Land Registry fees, search fees, etc.). Billed to the client separately from legal fees.",
  },

  // ═══════════════════════════════════════════════════════════════
  // INVOICE & PAYMENT
  // ═══════════════════════════════════════════════════════════════
  invoice_type: {
    title: "Invoice Type",
    description: "Interim = partial bill during matter. Final = closing bill. On Account = request for funds before work completed.",
  },
  vat_rate: {
    title: "VAT Rate",
    description: "Standard rate is 20%. Some services may be exempt or zero-rated. Check with Accounts if unsure.",
  },
  payment_method: {
    title: "Payment Method",
    description: "How funds will be transferred: BACS (2-3 days), CHAPS (same day, fee applies), or cheque.",
  },

  // ═══════════════════════════════════════════════════════════════
  // COMPLIANCE & AUDIT
  // ═══════════════════════════════════════════════════════════════
  backfill: {
    title: "Backfill Unfiled",
    description: "Batch process to file any conflict checks or risk assessments that weren't automatically saved to SharePoint. For audit compliance.",
  },
  deletion_audit: {
    title: "Deletion Audit",
    description: "Log of all deleted submissions, including who deleted them and why. Directors perform cascade hard-deletes that permanently remove the submission and all linked records (checklists, onboarding entries). Audit trail preserved for SRA compliance.",
  },
  sra_compliance: {
    title: "SRA Compliance",
    description: "Solicitors Regulation Authority requirements for record-keeping, client identification, and conflict management.",
  },
  audit_trail: {
    title: "Audit Trail",
    description: "Complete history of all actions taken on a matter. Shows who did what and when for compliance purposes.",
  },

  // ═══════════════════════════════════════════════════════════════
  // DOCUMENT ACTIONS
  // ═══════════════════════════════════════════════════════════════
  download_pdf: {
    title: "Download PDF",
    description: "Download this document to your computer as a PDF file. You can then print, email, or save it locally.",
  },
  view_sharepoint: {
    title: "View on SharePoint",
    description: "Opens this document in SharePoint. From there you can edit, share, or view version history.",
  },
  regenerate_pdf: {
    title: "Regenerate PDF",
    description: "Create a fresh PDF with the latest data. Use if details have changed since the original was generated.",
  },
  send_to_client: {
    title: "Send to Client",
    description: "Email this document to the client. They'll receive a secure link to view/download.",
  },
  copy_link: {
    title: "Copy Link",
    description: "Copy the SharePoint link to your clipboard. You can then paste it into an email or message.",
  },

  // ═══════════════════════════════════════════════════════════════
  // NEW MATTER FORM FIELDS
  // ═══════════════════════════════════════════════════════════════
  matter_type: {
    title: "Matter Type",
    description: "The category of legal work: Residential Conveyancing, Commercial Property, Remortgage, etc. Determines workflow and compliance requirements.",
  },
  client_type: {
    title: "Client Type",
    description: "Private Individual or Business/Organisation. Affects AML requirements and fee structure.",
  },
  property_address: {
    title: "Property Address",
    description: "The address of the property being purchased/sold/remortgaged. Used for searches and Land Registry applications.",
  },
  purchase_price: {
    title: "Purchase Price",
    description: "The agreed price for the property. Used to calculate SDLT, fees, and completion figures.",
  },
  mortgage_amount: {
    title: "Mortgage Amount",
    description: "The loan amount from the mortgage lender. Deducted from completion funds calculation.",
  },
  other_side_solicitor: {
    title: "Other Side's Solicitor",
    description: "The firm acting for the other party in the transaction. Correspondence and contracts go through them.",
  },
  estimated_completion: {
    title: "Estimated Completion",
    description: "Target date for completing the transaction. Used for planning and deadline tracking.",
  },
  referral_source: {
    title: "Referral Source",
    description: "How the client heard about us (estate agent, web search, recommendation). Used for marketing analysis.",
  },

  // ═══════════════════════════════════════════════════════════════
  // COMPLETIONS BOOK
  // ═══════════════════════════════════════════════════════════════
  completions_book: {
    title: "Completions Book",
    description: "Central register for tracking property completions. Records the status of signed documents, searches, exchange, redemption statements, completion statements, and client funds for each matter.",
    link: { text: "Open Completions Book", href: "/completions-book" },
  },
  cb_completion_date: {
    title: "Completion Date (Source of Truth)",
    description: "The agreed completion date, sourced from the Pre-Exchange Checklist item 5.1. Shown as read-only when linked. Changes require a mandatory reason and are fully audit-logged.",
  },
  cb_date_override: {
    title: "Change Completion Date",
    description: "Unlocks the read-only date field. You must enter a reason for the change. The system logs: old date, new date, who changed it, when, and why. An email notification is sent to the fee earner and supervising partner.",
  },
  cb_date_history: {
    title: "Completion Date History",
    description: "Audit trail of all completion date changes on this entry. Each record shows old date, new date, changed by, timestamp, and reason.",
  },
  cb_signed_docs: {
    title: "Signed Docs Received",
    description: "Whether all signed documents (TR1, mortgage deed, etc.) have been received from the client ahead of completion.",
  },
  cb_os_k_searches: {
    title: "OS1 & K17 Searches Done",
    description: "OS1 (priority search at Land Registry) and K17 (bankruptcy search). Both must be done shortly before completion to protect the buyer/lender.",
  },
  cb_exchanged: {
    title: "Exchanged",
    description: "Whether contracts have been exchanged. After exchange, parties are legally committed and the completion date is fixed.",
  },
  cb_redemption: {
    title: "Redemption Statement Received",
    description: "For sales and remortgages: a statement from the existing lender showing the amount needed to discharge the mortgage. An email reminder is sent ≤ 5 working days before completion if outstanding.",
  },
  cb_completion_stmt: {
    title: "Completion Statement Prepared",
    description: "Financial summary of all funds in/out at completion. An email reminder is sent ≤ 3 working days before completion if not finalised.",
  },
  cb_cot: {
    title: "CoT Submitted",
    description: "Certificate of Title — the solicitor's formal confirmation to the lender that the property and title are as described in the mortgage offer.",
  },
  cb_client_funds: {
    title: "Client Funds Requested",
    description: "Whether the client has been asked to send completion funds. A daily email reminder is sent while status is 'Requested' until resolved.",
  },
  cb_calendar: {
    title: "Calendar Heatmap",
    description: "Visual overview of completion volume by date. Darker colours = more completions. Click any day to filter the table to that date.",
  },
  cb_csv_export: {
    title: "Export CSV",
    description: "Download the current filtered Completions Book data as a CSV file for external reporting or backup.",
  },
  cb_reimport: {
    title: "Re-import Excel",
    description: "Upload the master Completions Book spreadsheet to refresh the data. Available to directors in the Directors' Lounge. Uses the same import logic as the original data load.",
  },

  // ═══════════════════════════════════════════════════════════════
  // PRE-EXCHANGE CHECKLIST
  // ═══════════════════════════════════════════════════════════════
  pre_exchange_checklist: {
    title: "Pre-Exchange Checklist",
    description: "Structured approval checklist covering title, searches, enquiries, mortgage, exchange readiness, and compliance. All items must be addressed before exchange.",
    link: { text: "Open checklist", href: "/pre-exchange-checklist" },
  },
  pre_exchange_5_1: {
    title: "Item 5.1 — Completion Date Agreed",
    description: "The agreed completion date entered here becomes the single source of truth. It flows automatically to the Completions Book and Pre-Completion Checklist. Changing it after exchange triggers an audit log and email notification.",
  },
  pre_exchange_signature: {
    title: "Fee Earner Signature",
    description: "The responsible solicitor's signature confirming all checklist items have been reviewed and the matter is ready for exchange.",
  },
  pre_exchange_supervisor: {
    title: "Supervisor Review",
    description: "Senior partner sign-off required before exchange can proceed. Includes review notes and approval status.",
  },

  // ═══════════════════════════════════════════════════════════════
  // PRE-COMPLETION CHECKLIST
  // ═══════════════════════════════════════════════════════════════
  pre_completion_checklist: {
    title: "Pre-Completion Checklist",
    description: "Structured checklist covering searches, documents, financials, undertakings, lender requirements, and final checks before completion.",
    link: { text: "Open checklist", href: "/pre-completion-checklist" },
  },
  pre_completion_8_1: {
    title: "Item 8.1 — Completion Date Confirmed",
    description: "Confirms the completion date and time with all parties. If this date differs from the Pre-Exchange date (item 5.1), a warning appears and a reason must be recorded.",
  },
  pre_completion_date_conflict: {
    title: "Date Conflict Warning",
    description: "Appears when the Pre-Completion completion date differs from the Pre-Exchange date. You must record a reason for the discrepancy. The change is logged and triggers a notification email to the fee earner and supervising partner.",
  },

  // ═══════════════════════════════════════════════════════════════
  // CROSS-REFERENCES
  // ═══════════════════════════════════════════════════════════════
  also_conflict_check: {
    title: "Conflict Check Required",
    description: "Before proceeding, you should run a conflict check to ensure no conflicts of interest.",
    link: { text: "Run conflict check", href: "/conflict-check" },
  },
  also_risk_assessment: {
    title: "Risk Assessment Required",
    description: "A risk assessment should be completed for compliance purposes.",
    link: { text: "Complete risk assessment", href: "/risk-assessment" },
  },
  also_onboarding: {
    title: "Client Onboarding",
    description: "The client needs to complete AML/KYC verification before work can begin.",
    link: { text: "View onboarding status", href: "/dashboard?tab=onboarding" },
  },
  data_cleanup: {
    title: "Data Cleanup",
    description: "Directors can purge test data from the system. Test records (identified by name/email patterns) are automatically filtered from the onboarding list. Bulk cleanup available via the admin cleanup tool.",
  },
  director_hard_delete: {
    title: "Director Hard-Delete",
    description: "Directors can permanently delete submissions with a cascade that removes all linked records (checklists, client onboarding entries). A mandatory reason is required and the deletion is fully audit-logged.",
  },
  deferred_conflict: {
    title: "Deferred Conflict Check",
    description: "When the other side is not yet known (e.g., seller without buyer), the conflict check can be deferred with a mandatory reason. All subsequent steps (OB, Thirdfort, FORF) remain available. The conflict check must be completed later and linked to the matter.",
  },
};

/**
 * InfoTooltip Component
 * A reusable help icon that shows explanatory text on hover/click.
 * 
 * Usage:
 *   <InfoTooltip id="conflict_check" />
 *   <InfoTooltip id="ob_status" size="sm" />
 */
export function InfoTooltip({ id, metric, size = "default", className = "" }) {
  // Support both 'id' and 'metric' props for backwards compatibility
  const contentId = id || metric;
  const content = HELP_CONTENT[contentId];
  
  if (!content) {
    return null;
  }
  
  const sizeClasses = {
    sm: "w-3 h-3",
    default: "w-4 h-4",
    lg: "w-5 h-5",
  };
  
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={`inline-flex items-center justify-center rounded-md hover:bg-slate-100 p-0.5 transition-colors focus:outline-none focus:ring-2 focus:ring-teal/20 ${className}`}
            data-testid={`info-${contentId}`}
          >
            <HelpCircle className={`${sizeClasses[size]} text-slate-400 hover:text-teal transition-colors`} />
          </button>
        </TooltipTrigger>
        <TooltipContent 
          side="top" 
          className="max-w-xs bg-white text-slate-700 border border-slate-200 shadow-lg p-3 z-50"
        >
          <p className="font-medium text-sm text-slate-800 mb-1">{content.title}</p>
          <p className="text-xs text-slate-600 leading-relaxed">{content.description}</p>
          {content.link && (
            <Link 
              to={content.link.href} 
              className="inline-flex items-center gap-1 text-xs text-teal hover:underline mt-2"
            >
              {content.link.text} <ExternalLink className="w-3 h-3" />
            </Link>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * ActionTooltip Component
 * For buttons and action items - explains what the action does
 */
export function ActionTooltip({ children, title, description, className = "" }) {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          {children}
        </TooltipTrigger>
        <TooltipContent 
          side="top" 
          className={`max-w-xs bg-slate-900 text-white border-0 shadow-lg p-2 z-50 ${className}`}
        >
          <p className="font-medium text-xs">{title}</p>
          {description && <p className="text-xs text-slate-300 mt-0.5">{description}</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Inline help text component for longer explanations
 */
export function HelpText({ id, className = "" }) {
  const content = HELP_CONTENT[id];
  if (!content) return null;
  
  return (
    <p className={`text-xs text-slate-500 mt-1 ${className}`}>
      {content.description}
    </p>
  );
}

export default InfoTooltip;
